import type { ClinicalSummaryTermReview } from '@/pages/order/clinicalSummaryReviewTypes'
import type { AnnotationReason } from '@/pages/order/livePhenotypeMerge'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'

/**
 * Figma Components §4 — reason badges on needs-input / settled rows. The relative reasons come only
 * from the summary's text, for rows the extraction did not annotate: it leaves relatives' findings out.
 */
export type ClinicalSummaryReasonKind =
  AnnotationReason | 'maternal-history' | 'family-history' | 'you-recorded-absent' | 'you-recorded-present'

const REASON_COPY: Record<ClinicalSummaryReasonKind, string> = {
  prenatal: 'Prenatal finding',
  'low-confidence': 'Low-confidence match',
  'family-history': 'Family history',
  'maternal-history': 'Maternal history',
  'you-recorded-absent': 'You recorded absent',
  'you-recorded-present': 'You recorded present',
}

const RELATIVE = String.raw`(?:father|sibling|brother|sister|cousin|aunt|uncle|grand(?:mother|father|parent))`
// The finding must be attributed to the relative: "History obtained from the mother" or
// "Mother reports…" names an informant, not a subject.
const MATERNAL_CUE = new RegExp(
  String.raw`\bmaternal\b(?!\s+${RELATIVE})|\bmother(?:'s)?\s+(?:has|had|with|history of)\b`
)
const FAMILY_CUE = new RegExp(
  String.raw`\bfamily (?:history|hx)\b|\bpaternal\b|\bmaternal\s+${RELATIVE}|\b${RELATIVE}(?:'s)?\s+(?:has|had|with)\b`
)
const PREGNANCY_CUE = /\bprenatal\b|\bpregnan/
// The extraction service's maternal-condition rule: in a pregnancy sentence these name the
// mother's condition unless the sentence is about the fetus or reports imaging.
const MATERNAL_CONDITION =
  /\b(?:infections?|diabet(?:es|ic)|hypertensi(?:on|ve)|high\s+blood\s+pressure|(?:pre-?)?eclampsia|hellp|chorioamnionitis|thyroid|hypothyroid(?:ism)?|hyperthyroid(?:ism)?)\b/
const FETAL_OR_IMAGING_CUE =
  /\b(?:fetal|foetal|fetus|foetus|congenital(?:ly)?|ultrasounds?|ultrasonograph(?:y|ic)|sonograph(?:y|ic)|sonograms?|scans?|imaging|mri)\b/

/**
 * A pregnancy sentence names the mother's own condition, so the quote is hers and not the
 * patient's — unless the sentence is about the fetus or reports imaging, which the pregnancy
 * sentence describes for the patient. Both arguments are already lowercased.
 *
 * The single place this rule lives: the row order and the reason badge must agree about whose
 * finding a row is, or a row sorted away from the patient's would still be badged as theirs.
 */
function citesMaternalPregnancyCondition(sentence: string, citedText: string): boolean {
  return PREGNANCY_CUE.test(sentence) && MATERNAL_CONDITION.test(citedText) && !FETAL_OR_IMAGING_CUE.test(sentence)
}

/**
 * The cited words are about the mother or a relative. Reads only the summary, so it gives the
 * same answer for a row the extraction did not annotate.
 */
export function citesRelativeFinding(
  contextText: string | null | undefined,
  citedText: string | null | undefined
): boolean {
  const sentence = (contextText ?? '').toLowerCase()
  if (MATERNAL_CUE.test(sentence) || FAMILY_CUE.test(sentence)) return true
  return citesMaternalPregnancyCondition(sentence, (citedText ?? '').toLowerCase())
}
/**
 * Map persisted review + optional extraction reason text to a Figma reason badge.
 * Returns null when no badge should show (Figma R10 — Flag reasons: None).
 */
export function resolveClinicalSummaryReason(input: {
  termReview?: ClinicalSummaryTermReview | null
  category?: CategorizedHPOOption['category']
  /** The summary now contradicts the presence/absence the clinician recorded. */
  contradiction?: boolean
  /** Ephemeral PhenoML `reason` / `rationale` from the last extraction (not persisted). */
  extractionReason?: string | null
  /** Sentence holding the term's citation (`citationSentence`); null when it has none. */
  contextText?: string | null
  /**
   * The words the row quotes. Defaults to the whole sentence, which is all a caller that only
   * remapped the sentence can offer; passing the quote keeps a patient finding that merely shares
   * a sentence with the mother's condition out of the maternal reading.
   */
  citedText?: string | null
  /**
   * The last extraction's `subject` / `flags` reason. Null when it annotated the term with no reason;
   * undefined when it did not annotate it, and only then is the text searched for cues.
   */
  annotationReason?: AnnotationReason | null
}): ClinicalSummaryReasonKind | null {
  const { termReview, category, contradiction, extractionReason, contextText, citedText, annotationReason } = input
  if (!termReview) return null

  if (contradiction) {
    return category === 'Excluded' ? 'you-recorded-absent' : 'you-recorded-present'
  }
  // The extraction leaves relatives' findings out, so a row it gave no reason to may still quote
  // the mother's: a pregnancy sentence naming a maternal condition is hers (Figma R06/R11/R12 badge
  // the hypertension row of "No prenatal infections, diabetes, hypertension, or thyroid disease."
  // as Maternal history). Judged before the annotation so the badge agrees with the row order,
  // which reads the same sentence the same way.
  if (
    annotationReason == null &&
    citesMaternalPregnancyCondition((contextText ?? '').toLowerCase(), (citedText ?? contextText ?? '').toLowerCase())
  ) {
    return 'maternal-history'
  }
  if (annotationReason !== undefined) return annotationReason

  const haystack = `${extractionReason ?? ''} ${contextText ?? ''}`.toLowerCase()
  if (MATERNAL_CUE.test(haystack)) return 'maternal-history'
  if (FAMILY_CUE.test(haystack)) return 'family-history'
  // Only when PhenoML (or similar) explicitly signals low confidence — bare
  // needs-input with no cue is Figma “Flag reasons: None”.
  if (/\blow[- ]confidence\b|\buncertain match\b/.test(haystack)) {
    return 'low-confidence'
  }

  return null
}

export function clinicalSummaryReasonLabel(kind: ClinicalSummaryReasonKind): string {
  return REASON_COPY[kind]
}
